import React, { PureComponent, ReactNode } from 'react';
import { DEFAULT_RANGE_SIZE, VirtualizedTableProps } from '../model';

import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  IndexRange,
  InfiniteLoader,
  KeyMapper,
  List,
  ListRowProps,
} from 'react-virtualized';

import * as Styled from './VirtualizedTable.styles';

import * as O from 'fp-ts/Option';
import { renderOptional } from '@shared/utils/render';
import { Link } from 'react-router-dom';
import { pipe } from 'fp-ts/function';
import { sequenceT } from 'fp-ts/Apply';
import { DebouncedLineLoader } from '@layout/loaders/line-loader/LineLoader';
import { Paragraph } from '@styles/shared';

import isEqual from 'lodash.isequal';

const MIN_ROW_HEIGHT = 45;

export class VirtualizedTable<T> extends PureComponent<VirtualizedTableProps<T>> {
  private readonly cache: CellMeasurerCache;

  private listRef: List | null = null;

  constructor(props: VirtualizedTableProps<T>) {
    super(props);

    this.cache = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: MIN_ROW_HEIGHT,
      keyMapper: this.cellMeasurerCacheKeyMapper,
    });
  }

  componentDidMount() {
    if (this.props.noFilter) {
      this.props.loadMore({});
    }
  }

  componentDidUpdate(prevProps: Readonly<VirtualizedTableProps<T>>) {
    if (!isEqual(prevProps.range.filter, this.props.range.filter)) {
      this.listRef?.scrollToPosition(0);
    }

    if (prevProps.range.items !== this.props.range.items) {
      this.cache.clearAll();
      this.listRef?.forceUpdateGrid();
    }
  }

  private cellMeasurerCacheKeyMapper: KeyMapper = (rowIndex: number, columnIndex: number) =>
    pipe(
      O.fromNullable(this.props.keyMapper),
      O.chain(keyMapper =>
        pipe(
          this.props.range.get(rowIndex),
          O.map(item => keyMapper(item, rowIndex)),
        ),
      ),
      O.getOrElse(() => `${rowIndex}-${columnIndex}-${this.props.range.has(rowIndex) ? 'loaded' : 'loading'}`),
    );

  private clearCellMeasurerCache = () => this.cache.clearAll();

  private isRowLoaded = ({ index }: Index): boolean => this.props.range.has(index);

  private loadMoreRows = ({ startIndex, stopIndex }: IndexRange) =>
    this.props.loadMore({ startIndex, endIndex: stopIndex });

  private rowRenderer = (props: ListRowProps): ReactNode => {
    const { range, rowLinkBuilder, onRowClick } = this.props;

    // @ts-ignore
    const scrollBarSize = props.parent._verticalScrollBarSize ?? 0;

    const item = range.get(props.index);

    const to = pipe(
      sequenceT(O.Apply)(item, O.fromNullable(rowLinkBuilder)),
      O.chainNullableK(([item, rowLinkBuilder]) => rowLinkBuilder(item, props.index)),
      O.toNullable,
    );

    const handleClick = () => {
      if (O.isSome(item) && onRowClick) {
        onRowClick(item.value, props.index);
      }
    };

    return (
      <CellMeasurer key={props.key} cache={this.cache} columnIndex={0} rowIndex={props.index} parent={props.parent}>
        {({ registerChild }) => (
          <Styled.VirtualizedTableRowWrapper
            as={to ? Link : undefined}
            to={to ?? ''}
            ref={registerChild as any}
            style={{ ...props.style, right: -scrollBarSize, width: `calc(100% + ${scrollBarSize}px)` }}
            loading={O.isNone(item)}
            hover={O.isSome(item) && !!(to || onRowClick)}
            onClick={handleClick}
          >
            {renderOptional(item, item => this.props.children(item, props.index))}
          </Styled.VirtualizedTableRowWrapper>
        )}
      </CellMeasurer>
    );
  };

  private noRowsRenderer = () =>
    this.props.range.loading ? (
      <></>
    ) : (
      <Styled.VirtualizedTableNoRow>
        <Paragraph size="small" color="tertiary" colorKey={500} weight="bold">
          {this.props.emptyMessage ?? 'Aucune donnée à afficher.'}
        </Paragraph>
      </Styled.VirtualizedTableNoRow>
    );

  private registerListRef = (infiniteLoaderRegister: (ref: List | null) => void) => (ref: List | null) => {
    infiniteLoaderRegister(ref);
    this.listRef = ref;
  };

  render() {
    const { range, header, scrollId } = this.props;

    return (
      <Styled.VirtualizedTableContainer>
        {header}
        <Styled.VirtualizedTableContent>
          {range.loading && <DebouncedLineLoader />}
          <InfiniteLoader
            rowCount={range.total}
            loadMoreRows={this.loadMoreRows}
            isRowLoaded={this.isRowLoaded}
            minimumBatchSize={DEFAULT_RANGE_SIZE}
            threshold={20}
          >
            {({ onRowsRendered, registerChild }) => (
              <AutoSizer onResize={this.clearCellMeasurerCache}>
                {({ width, height }) => (
                  <List
                    id={scrollId}
                    ref={this.registerListRef(registerChild)}
                    deferredMeasurementCache={this.cache}
                    rowCount={range.total}
                    rowHeight={this.cache.rowHeight}
                    estimatedRowSize={MIN_ROW_HEIGHT}
                    width={width}
                    height={height}
                    onRowsRendered={onRowsRendered}
                    noRowsRenderer={this.noRowsRenderer}
                    rowRenderer={this.rowRenderer}
                    style={{ outline: 'none', willChange: range.total === 0 ? 'unset' : 'transform' }}
                  />
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
        </Styled.VirtualizedTableContent>
      </Styled.VirtualizedTableContainer>
    );
  }
}
